import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { CampaignsContext } from "contexts/Campaigns";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Campaign, CampaignStatus } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { getParentRoute, isPage } from "utils/general";

import StepperHeader from "./SharedComponents/StepperHeader";
import CampaignBrandsV2 from "./Steps/CampaignBrands";
import CampaignDetailsV2 from "./Steps/CampaignDetails";
import CampaignReview from "./Steps/CampaignReview";
import CampaignTemplates from "./Steps/CampaignTemplates";
import { CampaignStep, CampaignStepOrders, getActiveStep } from "./schema";

export default function NewCampaign() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { campaign, setCampaign } = useContext(NewCampaignContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const { setCampaigns } = useContext(CampaignsContext);
  const { campaignId } = useParams();
  const { subscription } = useContext(SubscriptionContext);
  const [fetchingCampaign, setFetchingCampaign] = useState(!!campaignId);

  const handleClose = (exitManually: boolean) => {
    if (isPage(routes.onboardingPro)) {
      if (exitManually) {
        navigate(`/${routes.onboardingProCampaigns}${location.search}`);
      } else {
        navigate(`/${routes.onboardingProComplete}${location.search}`);
      }
      return;
    }
    navigate(`/${routes.campaignView}`);
  };

  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const activeStep = getActiveStep(location.pathname);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchCampaign = async () => {
    if (!campaignId) {
      redirect(CampaignStep.details);
      return;
    }
    if (!currentOrg) return;
    setFetchingCampaign(true);
    try {
      const { campaign, brandsCount, campaignBrands } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaignId}?per_page=10`,
        "GET",
      );
      setCampaign(campaign);
      if (brandsCount !== undefined) {
        setCampaign((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            campaignBrandCount: brandsCount,
          };
        });
      }
      if (campaignBrands !== undefined) {
        setCampaign((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            campaignBrands,
          };
        });
      }
      const stepInRoute = CampaignStepOrders.find((step) =>
        location.pathname.includes(step),
      );

      if (campaign?.status !== CampaignStatus.draft) {
        redirect(CampaignStep.review, campaign.id);
        return;
      }
      if (stepInRoute) {
        redirect(stepInRoute as CampaignStep, campaign?.id);
      } else {
        const lastActiveStep = getLastActiveStep(campaign, brandsCount);
        redirect(lastActiveStep || CampaignStep.details, campaign?.id);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchingCampaign(false);
    }
  };

  const getLastActiveStep = (campaign: Campaign, brandsCount: number) => {
    if (!(campaign?.name && campaign?.startDate)) {
      return CampaignStep.details;
    }
    if (!(brandsCount && brandsCount > 0)) {
      return CampaignStep.select_brands;
    }
    if (
      !(
        campaign?.emailSetting?.emailSettingTemplates &&
        campaign?.emailSetting?.emailSettingTemplates?.length > 0
      )
    ) {
      return CampaignStep.write_email;
    }
    return CampaignStep.review;
  };

  const handleUpdateCampaign = async (body: Map, skipMoveNext?: boolean) => {
    try {
      setSubmitLoading(true);
      const { campaign: updatedCampaign } = await fetcherAuth(
        getToken,
        campaign?.id
          ? `/api/organization/${currentOrg?.id}/campaigns/${campaign?.id}/draft`
          : `/api/organization/${currentOrg?.id}/campaigns/draft`,
        campaign?.id ? "PATCH" : "POST",
        {},
        body,
      );
      setCampaign(updatedCampaign);
      setCampaigns((prev) => {
        if (!prev) return [updatedCampaign];
        const existingIndex = prev.findIndex(
          (c) => c.id === updatedCampaign.id,
        );
        if (existingIndex !== -1) {
          const newCampaigns = [...prev];
          newCampaigns[existingIndex] = updatedCampaign;
          return newCampaigns;
        }
        return [...prev, updatedCampaign];
      });
      if (errorMessage) setErrorMessage("");
      if (!skipMoveNext) moveNext(updatedCampaign?.id);
    } catch (error) {
      if (error?.message) {
        setErrorMessage(error.message);
      }
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const moveNext = (campaignId?: number) => {
    if (isPage(CampaignStep.review)) {
      handleClose(false);
      return;
    }
    const currentStepIndex = getActiveStep(location.pathname);
    let stepName = "";
    if (currentStepIndex < CampaignStepOrders.length - 1) {
      stepName = CampaignStepOrders[currentStepIndex + 1];
    }
    if (stepName) {
      redirect(stepName as CampaignStep, campaignId || campaign?.id);
    }
  };

  const moveBack = () => {
    if (
      campaign?.status !== CampaignStatus.draft &&
      campaign?.status !== CampaignStatus.pending_approval
    ) {
      handleClose(false);
      return;
    }
    const currentStepIndex = getActiveStep(location.pathname);
    let stepName = "";
    if (currentStepIndex > 0) {
      stepName = CampaignStepOrders[currentStepIndex - 1];
    }
    if (stepName) {
      redirect(stepName as CampaignStep, campaign?.id);
    }
  };

  const redirect = (step: CampaignStep, campaignId?: number) => {
    const currentPathName = getParentRoute(location.pathname);
    if (campaignId) {
      navigate(`/${currentPathName}/campaign/${campaignId}/${step}`);
    } else {
      navigate(`/${currentPathName}/new/${step}`);
    }
  };

  useEffect(() => {
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, currentOrg?.id]);

  if (
    subscription?.remainingMessages !== undefined &&
    subscription.remainingMessages <= 0
  ) {
    return (
      <Dialog
        open={true}
        onClose={() => handleClose(true)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
          <Box
            component="i"
            className="fa-regular fa-envelope-circle-check"
            sx={{ mr: 1.5, color: "text.secondary" }}
          />
          No messages remaining
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.7 }}
          >
            You've used all your messages for this billing cycle. You won't be
            able to create or launch a campaign until your messages reset at the
            start of your next billing period.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => handleClose(false)}
            sx={{ textTransform: "none" }}
          >
            Go back
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (fetchingCampaign) {
    return (
      <Dialog
        fullWidth
        onClose={() => {}}
        maxWidth="lg"
        fullScreen={isMobileScreen}
        open={true}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 12,
          }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Loading campaign…
          </Typography>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      fullWidth
      onClose={() => {}}
      maxWidth="lg"
      fullScreen={isMobileScreen}
      title="Create A New Campaign"
      open={true}
    >
      <StepperHeader
        activeStep={activeStep}
        handleClose={() => handleClose(true)}
        title={campaign?.name ? `Campaign: ${campaign?.name}` : ""}
      />
      <Divider />

      <Routes>
        <Route
          path={CampaignStep.details}
          element={
            <CampaignDetailsV2
              handleClose={() => handleClose(true)}
              handleUpdateCampaign={handleUpdateCampaign}
              submitLoading={submitLoading}
            />
          }
        />
        <Route
          path={`${CampaignStep.select_brands}/*`}
          element={
            <CampaignBrandsV2
              handleUpdateCampaign={handleUpdateCampaign}
              moveBack={moveBack}
              moveNext={moveNext}
              submitLoading={submitLoading}
              errorMessage={errorMessage}
            />
          }
        />
        <Route
          path={`${CampaignStep.write_email}/*`}
          element={
            <CampaignTemplates
              handleUpdateCampaign={handleUpdateCampaign}
              moveBack={moveBack}
              submitLoading={submitLoading}
              errorMessage={errorMessage}
            />
          }
        />
        <Route
          path={`${CampaignStep.review}/*`}
          element={
            <CampaignReview
              handleUpdateCampaign={handleUpdateCampaign}
              moveBack={moveBack}
              submitLoading={submitLoading}
              errorMessage={errorMessage}
            />
          }
        />
      </Routes>
    </Dialog>
  );
}
